// Shared read-only recap strip for org division / branch / section cards.

import React from 'react';
import { SECTION_BG_LIGHTEN_RATIO, getStructureCardBgClass } from '../onboarding/onboardingStructureCardTheme';

const LEVEL_EYEBROW = {
  division: 'Division',
  branch: 'Branch',
  section: 'Section',
};

const RECAP_CLASSSETS = {
  ending: {
    root: 'members-onboarding-ending-structure',
    strip: 'members-onboarding-ending-structure__strip',
    cardItem: 'members-onboarding-ending-structure__card-item',
    cardSurface: 'members-onboarding-ending-structure__cardSurface',
    cardSurfaceNeutral: 'members-onboarding-ending-structure__cardSurface--neutral',
    cardHeader:
      'members-onboarding-structure__card-header members-onboarding-ending-structure__cardHeader--recap',
    cardLogo: 'members-onboarding-structure__card-logo',
    cardLogoPlaceholder:
      'members-onboarding-structure__card-logo members-onboarding-structure__card-logo--placeholder',
    cardHeadings: 'members-onboarding-structure__card-headings',
    cardEyebrow: 'members-onboarding-structure__breadcrumb-eyebrow',
    cardTitle: 'members-onboarding-structure__card-title',
    cardSubtitle: 'members-onboarding-structure__card-subtitle',
  },
  dashboard: {
    root: 'member-dashboard__structure-recap',
    strip: 'member-dashboard__structure-recap__strip',
    cardItem: 'member-dashboard__structure-recap__card-item',
    cardSurface: 'member-dashboard__structure-recap__card-surface',
    cardSurfaceNeutral: 'member-dashboard__structure-recap__card-surface--neutral',
    cardHeader: 'member-dashboard__structure-recap__card-header',
    cardLogo: 'member-dashboard__structure-recap__card-logo',
    cardLogoPlaceholder:
      'member-dashboard__structure-recap__card-logo member-dashboard__structure-recap__card-logo--placeholder',
    cardHeadings: 'member-dashboard__structure-recap__card-headings',
    cardEyebrow: 'member-dashboard__structure-recap__card-eyebrow',
    cardTitle: 'member-dashboard__structure-recap__card-title',
    cardSubtitle: 'member-dashboard__structure-recap__card-subtitle',
  },
};

/**
 * @param {'ending'|'dashboard'} [variant] — BEM namespace for recap cards.
 *   `ending` (default): onboarding ending + member-mgmt admin recap.
 *   `dashboard`: personal hub assignment strip (independent sizing hooks).
 */
export default function StructureRecapStrip({
  cards,
  dataResource = 'members.onboarding.ending',
  dataDescription = 'Structure recap',
  rootClassName = 'members-onboarding-ending-structure',
  variant = 'ending',
}) {
  if (!Array.isArray(cards) || cards.length === 0) return null;

  const classes = RECAP_CLASSSETS[variant] || RECAP_CLASSSETS.ending;
  const outerClassName =
    variant === 'dashboard'
      ? [classes.root, rootClassName].filter(Boolean).join(' ')
      : rootClassName;

  return (
    <div
      className={outerClassName}
      data-resource={dataResource}
      data-action="read"
      data-description={dataDescription}
    >
      <ul className={classes.strip}>
        {cards.map((card) => {
          const key = card.levelKey;
          const eyebrow = LEVEL_EYEBROW[key] || 'Unit';
          const lightenRatio = key === 'section' ? SECTION_BG_LIGHTEN_RATIO : 0;
          const bgClass = getStructureCardBgClass(card.elementColor1, card.elementColor2, lightenRatio);
          const surfaceClass = [
            classes.cardSurface,
            bgClass || classes.cardSurfaceNeutral,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <li key={`${key}-${card.id}`} className={classes.cardItem}>
              <div className={surfaceClass}>
                <div className={classes.cardHeader}>
                  {card.divisionImageUrl ? (
                    <img
                      className={classes.cardLogo}
                      src={card.divisionImageUrl}
                      alt=""
                      aria-hidden
                    />
                  ) : (
                    <span className={classes.cardLogoPlaceholder} aria-hidden />
                  )}
                  <div className={classes.cardHeadings}>
                    <span className={classes.cardEyebrow}>{eyebrow}</span>
                    <span className={classes.cardTitle}>{card.divisionFullName || '—'}</span>
                    {card.elementDetails ? (
                      <span className={classes.cardSubtitle}>{card.elementDetails}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
